import React, { FunctionComponent, Suspense, useEffect, useState } from 'react';

import { invert, map } from 'lodash';
import { Container, ListGroup, ListGroupItem } from 'react-bootstrap';
import { trackPromise } from 'react-promise-tracker';
import { Provider } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';

import { ActionId } from 'admin/actions/Actions';
import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { getSkillJarLink } from 'admin/users/Users';
import { AuthRoute, useProfile } from 'auth/Auth';
import { AppLink } from 'common/app-link/AppLink';
import useDeviceType, { DeviceType } from 'common/hooks/useDeviceType';
import { JobsProvider } from 'common/jobs/Jobs';
import { Looker } from 'common/looker/Looker';
import { MessageProvider } from 'common/message-bar/MessageBar';
import { Route as AppRoute, SurveysRoute as AppRouteSurveys } from 'core/util/Route';
import { Category, TrackActionType, useTrack } from 'core/util/Track';
import { Intermediate } from 'credentials/intermediate/Intermediate';
import { JOURNEY_INSIGHTS_USER_AUTH } from 'experience/Experience.constants';
import { ticketActionsStore } from 'tickets/actions2/common/actions-store';

import 'common/content/Content.scss';

const Admin = React.lazy(() => import('admin/Admin').then((module) => ({ default: module.Admin })));
const Actions = React.lazy(() => import('tickets/Actions').then((module) => ({ default: module.Actions })));
const TenantActions = React.lazy(() =>
    import('tickets/TenantActions').then((module) => ({ default: module.TenantActions })),
);
const ActionPlans = React.lazy(() =>
    import('action-plans/ActionPlansRoutes').then((module) => ({ default: module.ActionPlansRoutes })),
);
const CommandCenter = React.lazy(() =>
    import('command-center/CommandCenterRoute').then((module) => ({ default: module.CommandCenterRoute })),
);
const CompetitiveInsights = React.lazy(() =>
    import('competitive-insights/CompetitiveInsights').then((module) => ({ default: module.CompetitiveInsights })),
);
const Experience = React.lazy(() => import('experience/Experience').then((module) => ({ default: module.Experience })));
const Inbox = React.lazy(() => import('inbox/Inbox').then((module) => ({ default: module.Inbox })));
const Listings = React.lazy(() =>
    import('directory/listings/ListingsRoutes').then((module) => ({ default: module.ListingsRoutes })),
);
const NotFound = React.lazy(() => import('not-found/NotFound').then((module) => ({ default: module.NotFound })));
const Onboarder = React.lazy(() => import('onboarder/Onboarder').then((module) => ({ default: module.Onboarder })));
const IdeaPortal = React.lazy(() =>
    import('idea-portal/IdeaPortal').then((module) => ({ default: module.IdeaPortal })),
);
const Reports = React.lazy(() => import('reports/Reports').then((module) => ({ default: module.Reports })));
const ReportPrint = React.lazy(() =>
    import('reports/report-print/ReportPrint').then((module) => ({ default: module.ReportPrint })),
);
const Reviews = React.lazy(() => import('reviews/Reviews').then((module) => ({ default: module.Reviews })));
const Reviews2 = React.lazy(() => import('reviews2/Reviews.routes').then((module) => ({ default: module.Reviews })));
const Score = React.lazy(() => import('score/Score').then((module) => ({ default: module.ScoreX })));
const RSImpactReport = React.lazy(() =>
    import('score/rs-impact-report/RSDepartmentListingsReport').then((module) => ({
        default: module.RSDepartmentListingsReport,
    })),
);
const Screenshots = React.lazy(() =>
    import('screenshots/Screenshots').then((module) => ({ default: module.Screenshots })),
);
const SelfService = React.lazy(() =>
    import('self-service/SelfService').then((module) => ({ default: module.SelfService })),
);
const LocationCredentialMatcherResults = React.lazy(() =>
    import('location-credential-matcher-results/LocationCredentialMatcherResults').then((module) => ({
        default: module.LocationCredentialMatcherResults,
    })),
);
const Social = React.lazy(() => import('social/Social').then((module) => ({ default: module.Social })));
const Surveys = React.lazy(() => import('surveys/Surveys').then((module) => ({ default: module.Surveys })));
const SurveysPublic = React.lazy(() =>
    import('surveys-public/SurveysPublic').then((module) => ({ default: module.SurveysPublic })),
);
const UnifiedReporting = React.lazy(() =>
    import('flex-reports/UnifiedReportsRoute').then((module) => ({ default: module.UnifiedReportsRoute })),
);
const UserVoice = React.lazy(() => import('uservoice/UserVoice').then((module) => ({ default: module.UserVoice })));
const Widgets = React.lazy(() => import('widgets/Widgets').then((module) => ({ default: module.Widgets })));

const DemoRoutes = [
    {
        path: AppRoute.Dashboards2,
        code: AppRoute.Dashboards2,
    },
    {
        path: AppRoute.CommandCenter,
        code: AppRoute.CommandCenter,
    },
    {
        path: AppRoute.CompetitiveInsights,
        code: AppRoute.CompetitiveInsights,
    },
    {
        path: AppRoute.Experience,
        code: AppRoute.Experience,
    },
    {
        path: AppRoute.UnifiedReporting,
        code: AppRoute.UnifiedReporting,
    },
    {
        path: `${AppRoute.Inbox}/conversations`,
        code: `${AppRoute.Inbox}/conversations`,
    },
    {
        path: AppRoute.Reports2,
        code: AppRoute.Reports2,
    },
    {
        path: AppRoute.Reviews3,
        code: AppRoute.Reviews3,
    },
    {
        path: AppRoute.RSImpactReport,
        code: AppRoute.RSImpactReport,
    },
    {
        path: AppRoute.Score2,
        code: AppRoute.Score2,
    },
    <AuthRoute path={AppRoute.Tickets2}>
        <Actions />
    </AuthRoute>,
    <AuthRoute path={AppRoute.TenantActions}>
        <Provider store={ticketActionsStore}>
            <TenantActions />
        </Provider>
    </AuthRoute>,
    {
        path: `${AppRoute.Social2}/publisher`,
        code: `${AppRoute.Social2}/publisher`,
    },
    {
        path: `${AppRoute.Social2}/conversations`,
        code: `${AppRoute.Social2}/conversations`,
    },
    {
        path: AppRouteSurveys.ResultsFeedUi2,
        code: AppRouteSurveys.ResultsFeedUi2,
    },
];

export const Content: FunctionComponent = () => {
    const [, trackDispatch] = useTrack();
    const location = useLocation();
    const appRoutes = invert(AppRoute);
    const [DEVICE] = useDeviceType();
    const isDesktop = DEVICE === DeviceType.Desktop;

    const [currentPathname, setCurrentPathname] = useState<string>();
    const [currentApp, setCurrentApp] = useState<string>();

    // Track routes
    useEffect(() => {
        if (currentPathname !== location.pathname) {
            setCurrentPathname(location.pathname);

            const pathNodes = location.pathname.split('/');

            if (pathNodes.length > 1) {
                let app = appRoutes[`/${pathNodes[1]}`];

                if (app) {
                    // Ignore ui2 for tracking events
                    app = app.replace(/ui2/i, '');

                    // Update title
                    if (currentApp !== app) {
                        setCurrentApp(app);
                        document.title = `Reputation - ${app}`;
                    }

                    trackDispatch({
                        type: TrackActionType.Track,
                        event: {
                            app,
                            name: `${app.toLowerCase()}_view`,
                            properties: {
                                category: Category.PageView,
                                page: location.pathname,
                                referrer: window.location.href,
                                title: document.title,
                            },
                        },
                    });
                }
            }
        }
    }, [location, currentPathname, setCurrentPathname, currentApp, setCurrentApp, appRoutes, trackDispatch]);

    return (
        <div className="r4e-chrome-content" style={!isDesktop ? { marginTop: 0 } : {}}>
            <Suspense fallback={null}>
                <Switch>
                    <AuthRoute path={AppRoute.Admin}>
                        <Admin />
                    </AuthRoute>
                    <AuthRoute
                        path={AppRoute.CredentialsIntermediate}
                        auth={{ actions: [ActionId.PlatformTenants.View] }}
                    >
                        <Intermediate />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Dashboards2} auth={{ featureFlags: [FeatureFlagId.Reports2] }}>
                        <Reports />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.CommandCenter} auth={{ featureFlags: [FeatureFlagId.NovaCommandCenter] }}>
                        <CommandCenter />
                    </AuthRoute>
                    <AuthRoute
                        path={AppRoute.CompetitiveInsights}
                        auth={{
                            featureFlags: [FeatureFlagId.CompetitiveInsightsV3],
                            actions: [ActionId.CompetitiveInsightsBasic.View],
                        }}
                    >
                        <CompetitiveInsights />
                    </AuthRoute>
                    <AuthRoute
                        path={AppRoute.Experience}
                        auth={{
                            actions: [ActionId.AnalyticInsights.View].concat(JOURNEY_INSIGHTS_USER_AUTH.actions),
                        }}
                    >
                        <Experience />
                    </AuthRoute>
                    <AuthRoute
                        path={AppRoute.UnifiedReporting}
                        auth={{ featureFlags: [FeatureFlagId.NovaCommandCenter] }}
                    >
                        <UnifiedReporting />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.IdeaPortal}>
                        <IdeaPortal />
                    </AuthRoute>
                    <AuthRoute
                        path={AppRoute.Inbox}
                        auth={{
                            actions: [
                                ActionId.MessagingInboxConversations.View,
                                ActionId.SocialInboxConversations.View,
                                ActionId.SurveyInboxConversations.View,
                                ActionId.ReviewInboxConversations.View,
                            ],
                        }}
                    >
                        <Inbox />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Actions}>
                        <Actions />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Tickets2}>
                        <Actions />
                    </AuthRoute>
                    <AuthRoute
                        auth={{
                            mode: 'all',
                            featureFlags: [FeatureFlagId.TenantActionsReact, FeatureFlagId.TenantActionsYetiMigration],
                        }}
                        path={AppRoute.TenantActions}
                    >
                        <Provider store={ticketActionsStore}>
                            <TenantActions />
                        </Provider>
                    </AuthRoute>
                    <AuthRoute path={AppRoute.ActionPlans} auth={{ actions: [ActionId.ActionPlans.View] }}>
                        <ActionPlans />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Listings}>
                        <Listings />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Looker} auth={{ featureFlags: [FeatureFlagId.Looker] }}>
                        <div>
                            <Looker />
                        </div>
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Onboarder}>
                        <Onboarder />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Reports2} auth={{ featureFlags: [FeatureFlagId.Reports2] }}>
                        <Reports />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.ReviewsUi2} auth={{ actions: [ActionId.Reviews.View] }}>
                        <Reviews />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Reviews3} auth={{ actions: [ActionId.Reviews.View] }}>
                        <Reviews2 />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.RSImpactReport} auth={{ actions: [ActionId.AnalyticScore.View] }}>
                        <RSImpactReport />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Score2} auth={{ actions: [ActionId.AnalyticScore.View] }}>
                        <Score />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Social2}>
                        <Social />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Surveys3}>
                        <Surveys />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.UserVoice}>
                        <UserVoice />
                    </AuthRoute>
                    <AuthRoute path={AppRoute.Skilljar}>
                        <SkillJar />
                    </AuthRoute>
                    <AuthRoute path="/">
                        <Container fluid className="p-5">
                            <h1>Welcome, Developer!</h1>
                            <h2 className="mb-3">
                                Explore various <code>UI2</code> apps:
                            </h2>
                            <ListGroup className="mb-3">
                                {map(DemoRoutes, (routeItem: any, routeKey: any) => (
                                    <ListGroupItem key={routeKey} as={AppLink} path={routeItem.path} action>
                                        <code>{routeItem.code}</code>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </Container>
                    </AuthRoute>
                    <AuthRoute path="*">
                        <NotFound />
                    </AuthRoute>
                </Switch>
            </Suspense>
        </div>
    );
};

const SkillJar: FunctionComponent = () => {
    const [{ profile }] = useProfile();

    (async (): Promise<any> => {
        try {
            const sourceResult = await trackPromise(
                getSkillJarLink(profile?.tenant?.agencyID!, Number(profile?.tenant?.id), profile?.user.id!),
            );

            if (sourceResult?.url) {
                const skilljarSearchURI = window.location.href.split('?');
                const skillJarRedirect =
                    skilljarSearchURI && skilljarSearchURI[1]
                        ? `${sourceResult.url}&${skilljarSearchURI[1]}`
                        : sourceResult.url;

                window.location.href = skillJarRedirect;
                // window.open(sourceResult.url, '_blank');
            } else {
                window.location.href = window.location.origin;
            }
        } catch {
            window.location.href = window.location.origin;
        }
    })();

    return <></>;
};

export const PublicContent: FunctionComponent = () => {
    return (
        <div className="r4e-public-content">
            <Suspense fallback={null}>
                <Switch>
                    <Route path={AppRoute.Screenshots}>
                        <Screenshots />
                    </Route>
                    <Route path={AppRoute.SelfService}>
                        <SelfService />
                    </Route>
                    <Route path={AppRoute.LocationCredentialMatcherResults}>
                        <MessageProvider>
                            <JobsProvider>
                                <LocationCredentialMatcherResults />
                            </JobsProvider>
                        </MessageProvider>
                    </Route>
                    <Route path={AppRoute.Reports2Print} exact>
                        <ReportPrint />
                    </Route>
                    <Route path={AppRoute.Widgets2}>
                        <Widgets />
                    </Route>
                    <Route path={AppRoute.SurveysPublicUi2}>
                        <SurveysPublic />
                    </Route>
                    <Route path="*">
                        <NotFound />
                    </Route>
                </Switch>
            </Suspense>
        </div>
    );
};
